/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import DoctorFullForm from "components/forms/doctor/DoctorFullForm";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { TableProps } from "interfaces";
import { HeaderEnum, PharmacyInterface, ProviderInterface, UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";
import { FC, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewDoctorDataContent } from "utils/model.utils";
import { useTranslation } from "react-i18next";
import {
  selectIsDoctor,
  selectCurrentUser,
  selectUserPermissions,
  selectIsPatient,
} from "store/slices/auth.slice";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";

/**
 * Doctor/Provider table component for displaying and managing medical provider records.
 * 
 * Features:
 * - Displays provider information in sortable data table
 * - Columns: name, last name, specialty, action buttons
 * - Edit doctor functionality via modal with DoctorFullForm
 * - Delete doctor with confirmation dialog and list update
 * - Permission-based action visibility:
 *   - Doctors can only edit their own records
 *   - Patients cannot edit any records
 *   - Admins can edit all records
 * - Role-based filtering for appropriate data access
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure provider identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates current user ID against provider's user ID for self-edit
 * - Role checks via selectIsDoctor, selectIsPatient selectors
 * 
 * @param {TableProps<ProviderInterface>} props - Table properties
 * @param {ProviderInterface[]} props.modelList - Array of provider records to display
 * @param {Function} props.setModelList - Callback to update provider list after changes
 * 
 * @returns {JSX.Element} Provider data table with role-based edit/delete actions
 */
const DoctorTable: FC<TableProps<ProviderInterface>> = (props) => {
  const [selectedDoctor, setSelectedDoctor] =
    useState<ProviderInterface>(getNewDoctorDataContent());
  const [activeEditDoctorModal, setActiveEditDoctorModal] = useState<boolean>(false);

  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const permissions = useSelector(selectUserPermissions);
  const isProvider = useSelector(selectIsDoctor);
  const isPatient = useSelector(selectIsPatient);
  const [_isLoading, _error, sendRequest] = useHttp();

  useEffect(() => {
    if (selectedDoctor && selectedDoctor.id) {
      const updatedDoctor = props.modelList.find(d => d.id === selectedDoctor.id);
      if (updatedDoctor) {
        setSelectedDoctor(updatedDoctor);
      }
    }
  }, [props.modelList]);

  const deleteHandler = (providerTemplate: ProviderInterface) => {
    sendRequest<boolean, ProviderInterface>(
      CoreAPI_URLs.PROVIDER_GENERAL_OPERATIONS,
      onProviderDeleteHandler,
      "DELETE",
      JSON.stringify(providerTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: providerTemplate.id }],
      { payload: providerTemplate, successMessage: t("doctorTable.doctorDeleted") }
    );
  };
  
  const onProviderDeleteHandler = (result: boolean, provider: ProviderInterface) => {
    const indexList = props.modelList.indexOf(provider);
    props.modelList.splice(indexList, 1);
    props.setModelList([...props.modelList]);
  };

  const getUITable = (list: ProviderInterface[]) => {
    const baseColumns = [
      {
        Header: t("patientTable.name"),
        accessor: "person.name",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("patientTable.lastName"),
        accessor: "person.lastname",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("patientTable.specialty"),
        accessor: "specialty",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("patientTable.action"),
        accessor: "id",
        disableSortBy: true,

        Cell: ({ value, row }: any) => {
          if (isProvider) {
            const isOwnRecord = row.original.person?.user?.id === currentUser.id;
            if (!isOwnRecord) {
              return null;
            }
          }

          return (
            <>
              <PermissionWrapper
                requiredPermissions={[
                  PermissionsUser[EntityPermissions.DOCTORS].EDIT,
                  PermissionsUser[EntityPermissions.DOCTORS].VIEW,
                ]}
              >
                {!isPatient && (
                  <Button
                    id="cy-view-doctor-button"
                    variant="contained"
                    color="info"
                    sx={{ marginRight: 1 }}
                    onClick={() => {
                      const doctor = list.find((p) => p.id === value);
                      if (doctor) {
                        openModalHandler(doctor);
                      }
                    }}
                  >
                    {t("patientTable.view")}
                  </Button>
                )}
              </PermissionWrapper>

              <PermissionWrapper
                requiredPermissions={[
                  PermissionsUser[EntityPermissions.DOCTORS].DELETE,
                ]}
              >
                {!isPatient && (
                  <ButtonDialog
                    name="deleteDoctorButton"
                    onAcceptHandle={() => {
                      const doctor = list.find((p) => p.id === value);
                      if (doctor) {
                        if (doctor.person?.user?.id === currentUser.id) {
                          return;
                        }
                        deleteHandler(doctor);
                      }
                    }}
                    buttonText={t("doctorTable.deleteAction")}
                    title={t("doctorTable.deleteConfirmation")}
                    description={t("doctorTable.deleteDescription")}
                    labelledbyID={"DoctorDelete"}
                    describedbyID={"DoctorDescriptionId"}
                  />
                )}
              </PermissionWrapper>
            </>
          );
        },
      },
    ];

    const hasGenderPermission = permissions.includes(
      PermissionsUser[EntityPermissions.DOCTORS].VIEW
    );

    if (hasGenderPermission) {
      baseColumns.splice(2, 0, {
        Header: t("patientTable.sex"),
        accessor: "person.sex",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value ? t(`patientTable.${value}`) : ''} />,
      });
    }

    return {
      columns: baseColumns,
      rows: list,
    };
  };

  const openModalHandler = (doctor: ProviderInterface) => {
    setSelectedDoctor(doctor);
    setActiveEditDoctorModal(true);
  };

  const dataTableData = useMemo(
    () => getUITable(props.modelList),
    [props.modelList, isProvider, currentUser, permissions]
  );

  return (
    <>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
      />
      <Modal
        open={activeEditDoctorModal}
        onClose={() => {
          setActiveEditDoctorModal(false);
        }}
      >
        <DoctorFullForm
          isModal={true}
          isDoctor={true}
          modelList={props.modelList}
          setModelList={props.setModelList}
          model={selectedDoctor}
          action={FormActionType.MODIFY}
          onCloseAction={() => setActiveEditDoctorModal(false)}
          comesFromTable={true}
        />
      </Modal>
    </>
  );
};

export default DoctorTable;